import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';
import authService from '../auth/authService';
import { API_CONFIG } from '../config/api.config';

export interface StatewideSummary {
  totalFacilities: number;
  activeFacilities: number;
  connectedFacilities?: number;
  /** @deprecated Use connectedFacilities — fully integrated (Connected) active facilities */
  onboardedFacilities?: number;
  adoptionPercent: number;
  pendingOnboarding: number;
  totalStudies: number;
  pendingStudies: number;
  reportsGenerated: number;
  activeUsers: number;
  radiologistCount: number;
  technicianCount?: number;
  frontDeskCount?: number;
  averageTatMinutes: number;
  criticalTatMinutes: number;
  slaCompliancePercent: number;
  completedStudies: number;
  inactiveFacilities: number;
}

export interface TrendPoint {
  period: string;
  value: number;
}

export interface ModalityWorkloadItem {
  modality: string;
  count: number;
}

export interface FacilityPerformanceRow {
  facilityId: number;
  facilityName: string;
  city: string;
  facilityType: string;
  status: 'Active' | 'Inactive';
  studies: number;
  pending: number;
  adoptionPercent: number;
  avgTatMinutes: number | null;
  slaPercent: number | null;
  utilizationPercent: number | null;
}

export interface StatewideKpisData {
  summary: StatewideSummary;
  trend: TrendPoint[];
  modalityWorkload: ModalityWorkloadItem[];
  facilityPerformance: FacilityPerformanceRow[];
  compareMetrics?: FacilityPerformanceRow[];
  drillDetail?: FacilityPerformanceRow | null;
  kpiDrill?: {
    key: string;
    facilities: Array<{ facilityId: number; facilityName: string; value: number }>;
  };
  scope?: { type: string; lastSyncAt: string | null };
  filterOptions: {
    states: string[];
    districts: string[];
    facilities: { id: number; name: string }[];
    facilityTypes: string[];
    modalities: string[];
  };
}

export interface StatewideQuery {
  from?: string;
  to?: string;
  state?: string;
  district?: string;
  facilityId?: string;
  facilityType?: string;
  modality?: string;
  trendMetric?: string;
  trendPeriod?: string;
  compareFacilityIds?: string;
  drillFacilityId?: string;
  kpiKey?: string;
}

function buildQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '' && String(value) !== 'all') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getStatewideKpis(
  query: StatewideQuery
): Promise<{ success: boolean; data?: StatewideKpisData; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.ANALYTICS.STATEWIDE_KPIS}${buildQuery(query as Record<string, string | undefined>)}`;
    const response = await apiGet<{ success: boolean; data: StatewideKpisData }>(url, {
      requireAuth: true,
    });
    if (response.data?.success && response.data.data) {
      return { success: true, data: response.data.data };
    }
    return { success: false, error: 'Failed to load statewide KPIs' };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load statewide KPIs',
    };
  }
}

export async function exportStatewideKpis(
  query: StatewideQuery,
  format: 'csv' | 'pdf' | 'excel' = 'csv'
): Promise<void> {
  const url = `${API_ENDPOINTS.ANALYTICS.STATEWIDE_KPIS_EXPORT}${buildQuery({
    ...query,
    format,
  } as Record<string, string | undefined>)}`;
  const authHeader = authService.getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'text/csv, application/json',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
    mode: 'cors',
  });
  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `Statewide_KPI_Dashboard_${Date.now()}.csv`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
